/**
 * SortableCockpit hook — drag-to-reorder Cockpit widget grids.
 * Sends "reorder_widgets" event with the new order of widget IDs.
 *
 * Each widget card must carry `data-widget-id="<uuid>"` and contain an element
 * with `data-drag-handle` that acts as the grab handle. The whole card becomes
 * draggable only when the mousedown originates on the handle — this prevents
 * accidental drags when interacting with buttons inside the card.
 *
 * Routing: uses `pushEventTo(this.el, ...)` so the event respects the
 * `phx-target` of the host element. With `phx-target={@myself}` set on
 * the grid container, the event reaches a LiveComponent (e.g. Cockpit
 * PanelComponent); without it, falls back to the parent LiveView.
 *
 * IMPORTANT — why we use CSS `order` instead of DOM mutation:
 *   Each widget card contains an `<iframe>` (the widget app served by
 *   app_runner). Browsers reload an iframe whenever its DOM ancestor is
 *   reparented — i.e. `card.after(dragEl)` or LV diff repositioning the
 *   <article> would force the iframe to refetch, remount, and lose its
 *   in-memory state. The widget's freshly issued JWT also tends to race
 *   the cross-app auth check on remount, surfacing a 401 inside the
 *   widget's React ErrorBoundary ("Algo deu errado"). To avoid all of
 *   that, the DOM order of cards stays fixed (insertion order, controlled
 *   by `Seiva.Cockpit.Panels.list_items/1`); visual reorder happens
 *   purely via the inline `style="order: N"` set on each <article>.
 *
 * Robustness rewrite: previously the hook stashed `dragEl` and
 * `handleActive` in `_setupDrag`'s closure and re-bound `mouseup` on
 * `document` for every card every time `updated()` fired. Re-renders
 * during a drag (e.g. another LV diff arriving mid-drop) left dead
 * closures and zombie global listeners — and a half-finished drag
 * could fall through to the browser's default behaviour, which hangs
 * the page if the dataTransfer payload is empty. The new version
 * keeps state on `this`, binds the global mouseup once, and aborts
 * the drag explicitly when the host element is removed.
 */
export default {
  mounted() {
    this._dragEl = null
    this._handleActive = false
    this._activeCard = null

    this._onDocumentMouseUp = () => {
      // Always clear the drag flag on mouseup, anywhere on the page.
      this._handleActive = false
      if (this._activeCard) {
        this._activeCard.removeAttribute("draggable")
        this._activeCard = null
      }
      // Safety net: if a drag was started but `dragend` never fired
      // (rare browser quirks, killed iframe renderer, escape during
      // drag), make sure the iframes get unfrozen so they don't stay
      // pointer-events:none forever.
      if (this._dragEl) {
        this._dragEl.style.opacity = ""
        this._dragEl = null
        this._unfreezeIframes()
        this._disposeGhost()
      }
    }
    document.addEventListener("mouseup", this._onDocumentMouseUp)

    this._setupDrag()
  },

  updated() {
    this._setupDrag()
  },

  destroyed() {
    if (this._onDocumentMouseUp) {
      document.removeEventListener("mouseup", this._onDocumentMouseUp)
      this._onDocumentMouseUp = null
    }
    this._unfreezeIframes()
    this._disposeGhost()
    this._dragEl = null
    this._activeCard = null
    this._handleActive = false
  },

  // Promote every widget card to its own GPU compositor layer and
  // disable pointer-events on the iframes inside. While the drag is
  // active, layout reflows from CSS `order` changes only re-composite
  // the existing layer bitmaps — they don't repaint the iframe content,
  // and the embedded apps (e.g. AmCharts ResizeSensor) don't see any
  // resize storm. Without this, heavy widgets can hang the iframe
  // renderer (Chrome's "Aw, Snap!" page with code 5 = RESULT_CODE_HUNG).
  _freezeIframes() {
    const cards = this.el.querySelectorAll("[data-widget-id]")
    cards.forEach(card => {
      // Stash any existing transform/will-change so we can restore on
      // dragend (cards normally have neither, but be defensive).
      card._sortableSavedTransform = card.style.transform
      card._sortableSavedWillChange = card.style.willChange
      card.style.transform = "translateZ(0)"
      card.style.willChange = "transform"

      const iframe = card.querySelector("iframe")
      if (iframe) {
        iframe._sortableSavedPointer = iframe.style.pointerEvents
        iframe.style.pointerEvents = "none"
      }
    })
  },

  // Build a lightweight DOM node to use as the drag image. Letting the
  // browser snapshot the source card includes its iframe — and snapshot
  // of a busy iframe (charts animating, ResizeSensors firing) blocks
  // the main thread long enough that the renderer is killed. Returning
  // a tiny offscreen pill that just shows the widget title sidesteps
  // the whole snapshot path.
  _makeGhost(card) {
    try {
      const title =
        (card.querySelector("header p") && card.querySelector("header p").textContent.trim()) ||
        "Widget"
      const ghost = document.createElement("div")
      ghost.setAttribute("aria-hidden", "true")
      ghost.style.cssText = [
        "position: absolute",
        "top: -9999px",
        "left: -9999px",
        "padding: 8px 14px",
        "border-radius: 8px",
        "background: var(--bg-card, #fff)",
        "color: var(--text-primary, #0A2540)",
        "border: 1px solid var(--border-default, #E3E8EE)",
        "box-shadow: 0 8px 24px rgba(10,37,64,.18)",
        "font: 500 13px/1.4 Delight, system-ui, sans-serif",
        "white-space: nowrap",
        "pointer-events: none",
        "z-index: 2147483647"
      ].join(";")
      ghost.textContent = title
      return ghost
    } catch (_) {
      return null
    }
  },

  _disposeGhost() {
    if (this._ghostEl) {
      try { this._ghostEl.remove() } catch (_) {}
      this._ghostEl = null
    }
  },

  _unfreezeIframes() {
    const cards = this.el.querySelectorAll("[data-widget-id]")
    cards.forEach(card => {
      card.style.transform = card._sortableSavedTransform || ""
      card.style.willChange = card._sortableSavedWillChange || ""
      delete card._sortableSavedTransform
      delete card._sortableSavedWillChange

      const iframe = card.querySelector("iframe")
      if (iframe) {
        iframe.style.pointerEvents = iframe._sortableSavedPointer || ""
        delete iframe._sortableSavedPointer
      }
    })
  },

  // Sort cards by their current CSS `order` value (visual order). Falls
  // back to DOM order when `order` is unset or equal — same key as the
  // browser uses to lay out grid items.
  _visualOrder() {
    const cards = [...this.el.querySelectorAll("[data-widget-id]")]
    const indexed = cards.map((c, i) => {
      const o = parseInt(c.style.order, 10)
      return { card: c, order: Number.isFinite(o) ? o : i, domIdx: i }
    })
    indexed.sort((a, b) => (a.order - b.order) || (a.domIdx - b.domIdx))
    return indexed.map(x => x.card)
  },

  _setupDrag() {
    const container = this.el
    const self = this

    container.querySelectorAll("[data-widget-id]").forEach(card => {
      if (card._sortableBound) return
      card._sortableBound = true

      const handle = card.querySelector("[data-drag-handle]")
      if (handle) {
        handle.addEventListener("mousedown", () => {
          self._handleActive = true
          self._activeCard = card
          card.setAttribute("draggable", "true")
        })
      }

      card.addEventListener("dragstart", (e) => {
        if (!self._handleActive) {
          // Browser tried to drag without our consent (e.g. text
          // selection or an HTML5 drag from elsewhere) — abort,
          // otherwise the empty dataTransfer + drop on the page can
          // navigate away or freeze the LV.
          e.preventDefault()
          return
        }
        self._dragEl = card
        card.style.opacity = "0.4"
        self._freezeIframes()

        // Custom drag image — see _makeGhost() comment. Without this,
        // the browser snapshots the entire <article>, including a heavy
        // iframe (e.g. one running AmCharts animations) on the main
        // thread; that paint can hang the iframe renderer and crash it
        // ("Aw, Snap!" — Chrome RESULT_CODE_HUNG = code 5).
        const ghost = self._makeGhost(card)
        if (ghost) {
          self._ghostEl = ghost
          document.body.appendChild(ghost)
        }

        try {
          e.dataTransfer.effectAllowed = "move"
          e.dataTransfer.setData("text/plain", card.dataset.widgetId || "")
          if (ghost) e.dataTransfer.setDragImage(ghost, 12, 12)
        } catch (_err) {
          // Some browsers throw on dataTransfer access during a
          // synthetic drag; the move still works without setData.
        }
      })

      card.addEventListener("dragend", () => {
        card.style.opacity = ""
        card.removeAttribute("draggable")
        self._unfreezeIframes()
        self._disposeGhost()
        self._handleActive = false
        self._activeCard = null
        self._dragEl = null
        container.querySelectorAll("[data-widget-id]").forEach(c =>
          c.classList.remove("ring-2", "ring-[var(--accent-primary)]")
        )
      })

      card.addEventListener("dragover", (e) => {
        if (!self._dragEl) return
        e.preventDefault()
        try { e.dataTransfer.dropEffect = "move" } catch (_) {}
        if (card !== self._dragEl) {
          card.classList.add("ring-2", "ring-[var(--accent-primary)]")
        }
      })

      card.addEventListener("dragleave", () => {
        card.classList.remove("ring-2", "ring-[var(--accent-primary)]")
      })

      card.addEventListener("drop", (e) => {
        e.preventDefault()
        card.classList.remove("ring-2", "ring-[var(--accent-primary)]")
        const dragEl = self._dragEl
        if (!dragEl || dragEl === card || !document.contains(dragEl)) return

        // Compute new visual ordering without touching DOM positions.
        // Iframes inside the cards reload on any reparenting, which is
        // why we never call `card.after()` / `card.before()` here.
        const visual = self._visualOrder()
        const fromIdx = visual.indexOf(dragEl)
        const toIdx = visual.indexOf(card)
        if (fromIdx < 0 || toIdx < 0) return

        const reordered = visual.slice()
        reordered.splice(fromIdx, 1)
        // After removing dragEl, the target's index shifts left by 1
        // when fromIdx < toIdx — splice at toIdx still drops it on the
        // correct side because we're inserting at the position of the
        // item that *was* the target.
        reordered.splice(toIdx, 0, dragEl)

        // Apply new visual order via CSS `order` (no DOM mutation, so
        // iframes don't reload). Server will persist via "reorder_widgets"
        // and the next render keeps the inline style in sync.
        reordered.forEach((c, idx) => { c.style.order = idx })

        const newOrder = reordered
          .map(c => c.dataset.widgetId)
          .filter(Boolean)

        if (newOrder.length > 0) {
          self.pushEventTo(self.el, "reorder_widgets", { order: newOrder })
        }
      })
    })
  }
}
